import { useRef, useState } from "react";
import { Camera, Upload, RefreshCw, ArrowRight, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const MAX_MB = 10;
const ALLOWED = ["image/jpeg", "image/png", "image/webp"];

export default function UploadStep({ photo, setPhoto, onContinue }) {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState("");

  const handleFiles = (files) => {
    setError("");
    const f = files?.[0];
    if (!f) return;
    if (!ALLOWED.includes(f.type)) {
      setError("Please choose a JPG, PNG or WEBP image.");
      toast.error("Unsupported file type", { description: "Use JPG, PNG or WEBP." });
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      setError(`That image is over ${MAX_MB}MB. Try a smaller one.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const img = new Image();
      img.onload = () => {
        if (img.width < 300 || img.height < 300) {
          setError("That photo may be too difficult to work with. Try a clearer photo showing more of your dog.");
          return;
        }
        setPhoto({ dataUrl, width: img.width, height: img.height, name: f.name });
      };
      img.onerror = () => {
        setError("We couldn't load that photo. Please try another image.");
      };
      img.src = dataUrl;
    };
    reader.onerror = () => setError("We couldn't load that photo. Please try another image.");
    reader.readAsDataURL(f);
  };

  return (
    <div className="max-w-2xl mx-auto px-4 pb-16" data-testid="upload-step">
      <div className="text-center fade-up">
        <span className="text-xs font-bold tracking-widest uppercase text-[#c84125]">
          Step 1 · Photo
        </span>
        <h1 className="font-display mt-3 text-3xl sm:text-4xl font-extrabold text-[#0f172a] tracking-tight">
          Upload a clear photo of your dog.
        </h1>
        <p className="mt-3 text-sm sm:text-base text-[#475569] leading-relaxed">
          Front or 3/4 views in good light work best. The AI keeps your dog's face, colouring and markings — only the grooming changes.
        </p>
      </div>

      {!photo && (
        <div
          data-testid="upload-dropzone"
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            handleFiles(e.dataTransfer.files);
          }}
          className={`mt-8 rounded-3xl border-2 border-dashed p-8 sm:p-12 text-center transition-all duration-300 ${
            dragging
              ? "border-[#ff6b4a] bg-[#fff0ed] scale-[1.01]"
              : "border-[#eae4d9] bg-white shadow-[0_4px_20px_-2px_rgba(15,23,42,0.04)] hover:shadow-[0_12px_32px_-4px_rgba(15,23,42,0.08)]"
          } fade-up delay-1`}
        >
          <div className="mx-auto w-16 h-16 rounded-2xl bg-[#f3efe6] flex items-center justify-center">
            <Upload className="w-7 h-7 text-[#0f172a]" />
          </div>
          <p className="mt-5 text-sm text-[#475569]">
            Drop a photo here or choose from your device.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              data-testid="upload-photo-btn"
              onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-2 rounded-full bg-[#ff6b4a] hover:bg-[#e85535] text-white font-semibold px-6 py-3 shadow-md shadow-[#ff6b4a]/25 transition active:scale-[0.98]"
            >
              <Upload className="w-4 h-4" /> Upload Photo
            </button>
            <button
              data-testid="take-photo-btn"
              onClick={() => cameraRef.current?.click()}
              className="inline-flex items-center gap-2 rounded-full bg-white hover:bg-[#f3efe6] text-[#0f172a] font-medium px-5 py-3 border border-[#eae4d9] transition"
            >
              <Camera className="w-4 h-4" /> Take Photo
            </button>
          </div>
          <p className="mt-6 text-xs text-[#64748b]">JPG, PNG or WEBP · up to {MAX_MB}MB</p>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="sr-only"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="upload-file-input"
          />
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="sr-only"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="camera-file-input"
          />
        </div>
      )}

      {photo && (
        <div className="mt-8 rounded-3xl border border-[#eae4d9] bg-white p-3 sm:p-4 shadow-[0_18px_45px_-12px_rgba(15,23,42,0.18)] fade-up">
          <div className="relative rounded-2xl overflow-hidden bg-[#f3efe6] aspect-square sm:aspect-[4/3]">
            <img
              data-testid="uploaded-photo-preview"
              src={photo.dataUrl}
              alt="Uploaded dog"
              className="w-full h-full object-cover"
            />
            {photo.fromProfile && (
              <span
                data-testid="saved-photo-badge"
                className="absolute top-3 left-3 rounded-full bg-[#0f172a]/85 backdrop-blur text-white text-[10px] font-bold tracking-widest uppercase px-3 py-1.5"
              >
                Saved photo
              </span>
            )}
          </div>
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <button
              data-testid="change-photo-btn"
              onClick={() => {
                setPhoto(null);
                setError("");
              }}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-white hover:bg-[#f3efe6] text-[#0f172a] font-medium px-5 py-3 border border-[#eae4d9] transition"
            >
              <RefreshCw className="w-4 h-4" /> {photo.fromProfile ? "Use a new photo" : "Change Photo"}
            </button>
            <button
              data-testid="continue-to-style-btn"
              onClick={onContinue}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#ff6b4a] hover:bg-[#e85535] text-white font-semibold px-6 py-3 shadow-md shadow-[#ff6b4a]/25 transition active:scale-[0.98]"
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {error && (
        <div
          data-testid="upload-error"
          className="mt-4 flex items-start gap-3 rounded-2xl border border-[#ffd8cf] bg-[#fff0ed] p-4"
        >
          <AlertCircle className="w-5 h-5 text-[#c84125] shrink-0 mt-0.5" />
          <p className="text-sm text-[#7a2a1a]">{error}</p>
        </div>
      )}
    </div>
  );
}
